import {brandForHostname} from "hostnamePolicy";
import {DEFAULT_LOCALE, type Locale} from "i18n/localePreference";
import type {SiteLocale} from "seo/siteMeta";
import {brandAddress, DEPLOYED_BRANDS} from "../deployedTopology.mjs";

/**
 * The support inbox to name to the visitor, derived from the storefront they are actually on
 * (kitecorp/transferify-ui#2261). React-free by mandate (epic #26).
 *
 * The api routes support mail to the BRAND's own group — `support@transferify.ro` for transferify.ro
 * (api#1481), and for the main brand `support@transferify.eu` once kitecorp/transferify-api-service#2650
 * moves its `support-email` there at the flip — and names that address
 * in its delivery-failed error (api#1487). Before this module the ui published one hardcoded `.ro`
 * address from its catalogs, so every visitor of transferify.cloud was told to write to an inbox
 * their own request would not have been routed to.
 *
 * The storefront comes from the one host→brand rule (`hostnamePolicy.brandForHostname`,
 * kitecorp/transferify-ui#3470) rather than a second `transferify\.(eu|ro)` match here: two rules answering "which brand am I on" is how they end up
 * disagreeing, and that one already knows about `www.`, about `dev.` and about a customer's claimed
 * subdomain belonging to the brand it sits under (devops#142).
 */

/**
 * The local part, identical for every brand — each is a Google Group named `support@<brand>`.
 *
 * A brand whose inbox is not `support@` would need a per-brand table here, the same way api#1481 put
 * the address on the brand's configuration rather than deriving it from the brand key. Today there
 * is no such brand, and a table restating the same word once per market would be the harder thing to
 * keep true. The DOMAIN is the brand registry's (kitecorp/transferify-ui#2833).
 */
const SUPPORT_MAILBOX = "support";

/**
 * The support address for the page being served.
 *
 * A storefront subfolder of the main host is that market's storefront (kitecorp/transferify-api-service#2638),
 * and the api routes a support request made from it to that market's own inbox, so it is named here too:
 * `transferify.eu/ro/contact` names the Romanian storefront's queue. Anywhere else the host's own brand
 * decides, and a host that carries no brand gets the fallback market's inbox.
 *
 * @param hostname `window.location.hostname`, or `""` before mount. Callers in React take it from
 *   {@link ../utils/useMountedHostname.useMountedHostname} through {@link ./useSupportAddress}.
 * @param storefrontSubfolder the page's storefront subfolder (`i18n` context `storefrontSubfolder`), or
 *   `null` outside one and before mount
 * @param fallbackLocale the market whose inbox to name when neither the subfolder nor the host names a
 *   brand. Three real cases reach it: the pre-render and the first client render (which see `""` by
 *   design, see `utils/useMountedHostname`), a development machine, and a Premium BYO custom domain.
 *
 *   The support and withdrawal dialogs keep the default, the DEFAULT locale's brand, which is what `siteLinkOrigin` already
 *   hands a BYO custom domain and what the api falls back to as well (`brands.fallback-key:
 *   transferify.cloud`, the main brand's key), so the inbox named is the one the api would have routed
 *   that same request to.
 *
 *   `ContactBody` passes its DOCUMENT's locale instead (kitecorp/transferify-ui#2264), on the pre-rendered
 *   `/contact` page and in the Resources dialog's contact panel alike.
 *   The page is rendered once per locale tree and one tree is served on more than one host (the Romanian tree
 *   answers transferify.ro and the main host's `/ro` storefront), so its static HTML names the inbox of
 *   the market the tree is published in, which is the address a crawler indexes, and the subfolder or the
 *   host takes over once the page has mounted.
 */
export function supportAddressFor(
	hostname: string,
	storefrontSubfolder: Locale | null = null,
	fallbackLocale: SiteLocale = DEFAULT_LOCALE,
): string {
	const market = storefrontSubfolder ?? brandForHostname(hostname, DEPLOYED_BRANDS)?.locale ?? fallbackLocale;
	return brandAddress(SUPPORT_MAILBOX, market);
}
